((root, factory) => {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.CorkopiaHabitatEngine = api;
})(typeof globalThis !== 'undefined' ? globalThis : window, () => {
  'use strict';

  const VERSION = 'habitat-engine-v1';
  const BUILD_DNA_VERSION = 'build-dna-v4';
  const FIT = Object.freeze({
    FIT: 'FIT',
    FIT_WITH_WARNING: 'FIT_WITH_WARNING',
    DOES_NOT_FIT: 'DOES_NOT_FIT',
    UNKNOWN: 'UNKNOWN'
  });
  const CONCEPT_STATES = Object.freeze([
    'NOT_REQUESTED',
    'READY',
    'GENERATING',
    'COMPLETE',
    'FAILED',
    'EXPIRED'
  ]);
  const DISCLAIMER = 'Concept visualization only. Final appearance may vary. Product measurements remain authoritative.';
  const MAX_PIECES = 60;
  const MAX_HISTORY = 40;
  const INCHES_PER_CM = 1 / 2.54;

  const ENCLOSURE_CATALOG = Object.freeze([
    Object.freeze({
      id: 'custom',
      brand: 'Custom',
      model: 'Your measured enclosure',
      internalDimensions: null,
      verificationStatus: 'CUSTOMER_MEASUREMENT_REQUIRED',
      source: 'customer'
    }),
    Object.freeze({
      id: 'zoo_med_fixture',
      brand: 'Zoo Med',
      model: 'Select a model, then confirm internal measurements',
      internalDimensions: null,
      verificationStatus: 'FIXTURE_UNVERIFIED',
      source: 'fixture'
    }),
    Object.freeze({
      id: 'exo_terra_fixture',
      brand: 'Exo Terra',
      model: 'Select a model, then confirm internal measurements',
      internalDimensions: null,
      verificationStatus: 'FIXTURE_UNVERIFIED',
      source: 'fixture'
    })
  ]);

  const SPECIES_PROFILES = Object.freeze({
    version: 'species-profiles-fixture-v1',
    authority: 'FIXTURE_NOT_HUSBANDRY_GUIDANCE',
    profiles: Object.freeze([
      Object.freeze({
        id: 'arboreal_fixture',
        label: 'Arboreal project',
        status: 'FIXTURE_UNAPPROVED',
        questions: ['climbing', 'coverage', 'service_access'],
        goalPrompts: ['Vertical structure', 'Open climbing routes', 'Accessible maintenance']
      }),
      Object.freeze({
        id: 'terrestrial_fixture',
        label: 'Terrestrial project',
        status: 'FIXTURE_UNAPPROVED',
        questions: ['ground_cover', 'hides', 'service_access'],
        goalPrompts: ['Ground-level structure', 'Sheltered areas', 'Accessible maintenance']
      }),
      Object.freeze({
        id: 'plant_fixture',
        label: 'Plant or orchid project',
        status: 'FIXTURE_UNAPPROVED',
        questions: ['mounting', 'display', 'service_access'],
        goalPrompts: ['Mounting surfaces', 'Visual balance', 'Watering access']
      }),
      Object.freeze({
        id: 'other',
        label: 'Other project',
        status: 'CUSTOMER_DEFINED',
        questions: ['goals', 'service_access'],
        goalPrompts: []
      })
    ])
  });

  const clone = (value) => JSON.parse(JSON.stringify(value));
  const finite = (value) => Number.isFinite(Number(value)) ? Number(value) : null;
  const positive = (value) => {
    const parsed = finite(value);
    return parsed !== null && parsed > 0 ? parsed : null;
  };
  const nonNegative = (value) => {
    const parsed = finite(value);
    return parsed !== null && parsed >= 0 ? parsed : 0;
  };
  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, Number(value) || 0));
  const unique = (values) => [...new Set((values || []).filter(Boolean))];
  const idFor = (value) => String(value ?? '').trim();
  const round = (value, precision = 3) => Number(Number(value).toFixed(precision));
  const humanize = (value) => String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

  const toInches = (value, unit = 'in') => {
    const parsed = positive(value);
    if (parsed === null) return null;
    return round(unit === 'cm' ? parsed * INCHES_PER_CM : parsed);
  };
  const distanceInches = (value, unit = 'in') => {
    const parsed = nonNegative(value);
    return round(unit === 'cm' ? parsed * INCHES_PER_CM : parsed);
  };

  const normalizeDimensions = (input = {}, unit = input.unit || 'in') => ({
    width: toInches(input.width, unit),
    depth: toInches(input.depth, unit),
    height: toInches(input.height, unit),
    unit: 'in'
  });

  const dimensionsComplete = (dimensions) =>
    [dimensions?.width, dimensions?.depth, dimensions?.height].every((value) => positive(value) !== null);

  const createEnclosure = (input = {}) => {
    const catalog = ENCLOSURE_CATALOG.find((entry) => entry.id === input.catalogId) || ENCLOSURE_CATALOG[0];
    const unit = input.unit || 'in';
    const featureUnit = input.featureUnit || unit;
    const internalDimensions = normalizeDimensions(input.internalDimensions || input.dimensions || {}, unit);
    const confirmed = input.customerConfirmed === true && dimensionsComplete(internalDimensions);
    const normalizeFeatureZone = (zone = {}) => ({
      ...zone,
      x: distanceInches(zone.x, featureUnit),
      y: distanceInches(zone.y, featureUnit),
      z: distanceInches(zone.z, featureUnit),
      width: distanceInches(zone.width, featureUnit),
      height: distanceInches(zone.height, featureUnit),
      depth: distanceInches(zone.depth, featureUnit)
    });
    return {
      catalogId: catalog.id,
      brand: catalog.brand,
      model: input.model || catalog.model,
      source: catalog.source,
      internalDimensions,
      measurementStatus: confirmed ? 'CUSTOMER_CONFIRMED' : 'UNCONFIRMED',
      customerConfirmed: confirmed,
      access: {
        type: input.access?.type || 'front_doors',
        doorClearance: distanceInches(input.access?.doorClearance, featureUnit),
        lidClearance: distanceInches(input.access?.lidClearance, featureUnit),
        serviceClearance: distanceInches(input.access?.serviceClearance, featureUnit)
      },
      features: {
        drainageHeight: distanceInches(input.features?.drainageHeight, featureUnit),
        substrateHeight: distanceInches(input.features?.substrateHeight, featureUnit),
        backgroundDepth: distanceInches(input.features?.backgroundDepth, featureUnit),
        sideClearance: distanceInches(input.features?.sideClearance, featureUnit),
        topClearance: distanceInches(input.features?.topClearance, featureUnit),
        frontClearance: distanceInches(input.features?.frontClearance, featureUnit),
        vents: Array.isArray(input.features?.vents) ? input.features.vents.slice(0, 12).map(normalizeFeatureZone) : [],
        equipment: Array.isArray(input.features?.equipment) ? input.features.equipment.slice(0, 20).map(normalizeFeatureZone) : [],
        noBuildZones: Array.isArray(input.features?.noBuildZones) ? input.features.noBuildZones.slice(0, 30).map(normalizeFeatureZone) : []
      },
      assumptions: [
        'Dimensions are internal clear-space measurements supplied or confirmed by the customer.',
        'Rectangular geometry is a conservative proxy; doors, vents, fixtures, and irregular walls require explicit protected zones.'
      ]
    };
  };

  const normalizeZone = (zone = {}, limits = {}) => ({
    id: idFor(zone.id) || 'zone',
    label: zone.label || 'Protected zone',
    reason: zone.reason || 'CUSTOMER_DEFINED',
    x: clamp(nonNegative(zone.x), 0, limits.width || Infinity),
    y: clamp(nonNegative(zone.y), 0, limits.height || Infinity),
    z: clamp(nonNegative(zone.z), 0, limits.depth || Infinity),
    width: clamp(nonNegative(zone.width), 0, limits.width || Infinity),
    height: clamp(nonNegative(zone.height), 0, limits.height || Infinity),
    depth: clamp(nonNegative(zone.depth), 0, limits.depth || Infinity)
  });

  const calculateUsableSpace = (rawEnclosure = {}) => {
    const enclosure = createEnclosure(rawEnclosure);
    const internal = enclosure.internalDimensions;
    if (!enclosure.customerConfirmed || !dimensionsComplete(internal)) {
      return {
        status: 'UNKNOWN',
        reasonCodes: ['INTERNAL_DIMENSIONS_UNCONFIRMED'],
        enclosure,
        internal,
        usable: null,
        offsets: null,
        protectedZones: [],
        assumptions: enclosure.assumptions
      };
    }
    const features = enclosure.features;
    const floorLoss = features.drainageHeight + features.substrateHeight;
    const widthLoss = features.sideClearance * 2;
    const depthLoss = features.backgroundDepth + features.frontClearance;
    const heightLoss = floorLoss + features.topClearance;
    const usable = {
      width: round(Math.max(0, internal.width - widthLoss)),
      depth: round(Math.max(0, internal.depth - depthLoss)),
      height: round(Math.max(0, internal.height - heightLoss)),
      unit: 'in'
    };
    const offsets = {
      x: round(features.sideClearance),
      y: round(floorLoss),
      z: round(features.backgroundDepth)
    };
    const protectedZones = [];
    if (enclosure.access.type.includes('front') && enclosure.access.doorClearance > 0) {
      protectedZones.push({
        id: 'door-swing',
        label: 'Front door clearance',
        reason: 'DOOR_ACCESS',
        x: 0,
        y: 0,
        z: Math.max(0, internal.depth - enclosure.access.doorClearance),
        width: internal.width,
        height: internal.height,
        depth: enclosure.access.doorClearance
      });
    }
    if (enclosure.access.type.includes('top') && enclosure.access.lidClearance > 0) {
      protectedZones.push({
        id: 'lid-clearance',
        label: 'Top opening clearance',
        reason: 'LID_ACCESS',
        x: 0,
        y: Math.max(0, internal.height - enclosure.access.lidClearance),
        z: 0,
        width: internal.width,
        height: enclosure.access.lidClearance,
        depth: internal.depth
      });
    }
    features.vents.forEach((zone, index) => protectedZones.push(normalizeZone({
      ...zone,
      id: zone.id || `vent-${index + 1}`,
      label: zone.label || `Vent ${index + 1}`,
      reason: 'VENT_CLEARANCE'
    }, internal)));
    features.equipment.forEach((zone, index) => protectedZones.push(normalizeZone({
      ...zone,
      id: zone.id || `equipment-${index + 1}`,
      label: zone.label || `Equipment ${index + 1}`,
      reason: 'EQUIPMENT_CLEARANCE'
    }, internal)));
    features.noBuildZones.forEach((zone, index) => protectedZones.push(normalizeZone({
      ...zone,
      id: zone.id || `no-build-${index + 1}`,
      reason: zone.reason || 'NO_BUILD_ZONE'
    }, internal)));
    const translatedZones = protectedZones.map((zone) => {
      const start = {
        x: Math.max(0, zone.x - offsets.x),
        y: Math.max(0, zone.y - offsets.y),
        z: Math.max(0, zone.z - offsets.z)
      };
      const end = {
        x: Math.min(usable.width, zone.x + zone.width - offsets.x),
        y: Math.min(usable.height, zone.y + zone.height - offsets.y),
        z: Math.min(usable.depth, zone.z + zone.depth - offsets.z)
      };
      return {
        ...zone,
        x: round(start.x),
        y: round(start.y),
        z: round(start.z),
        width: round(Math.max(0, end.x - start.x)),
        height: round(Math.max(0, end.y - start.y)),
        depth: round(Math.max(0, end.z - start.z))
      };
    }).filter((zone) => zone.width > 0 && zone.height > 0 && zone.depth > 0);
    return {
      status: dimensionsComplete(usable) ? 'READY' : 'INVALID',
      reasonCodes: dimensionsComplete(usable) ? [] : ['NO_USABLE_VOLUME'],
      enclosure,
      internal,
      usable,
      offsets,
      protectedZones: translatedZones,
      assumptions: enclosure.assumptions
    };
  };

  const measurementValue = (input) => {
    if (input && typeof input === 'object') return positive(input.value);
    return positive(input);
  };

  const productDimensions = (product = {}) => {
    const measurements = product.measurements || {};
    const dimensions = product.dimensions || {};
    return {
      width: measurementValue(measurements.maximumWidth ?? dimensions.width),
      depth: measurementValue(measurements.maximumLength ?? dimensions.depth ?? dimensions.length),
      height: measurementValue(measurements.maximumHeight ?? dimensions.height),
      unit: 'in'
    };
  };

  const productIdentity = (product = {}) => ({
    pieceId: idFor(product.identity?.pieceId || product.pieceId),
    productId: idFor(product.identity?.shopifyProductId || product.productId),
    variantId: idFor(product.commerce?.selectedVariant?.id || product.identity?.shopifyVariantId || product.variantId),
    handle: idFor(product.identity?.handle || product.handle),
    title: product.identity?.title || product.title || 'Exact cork piece',
    family: product.classification?.family || product.family || null,
    image: product.media?.heroImage?.url || product.media?.heroImage?.previewUrl || product.image || null,
    priceCents: finite(product.commerce?.priceCents ?? product.priceCents),
    available: product.availability?.sellable === true || product.available === true,
    exactPiece: product.classification?.exactPiece === true || product.exactPiece === true
  });

  const publicEligible = (product = {}) => {
    const identity = productIdentity(product);
    return identity.available
      && identity.exactPiece
      && Boolean(identity.variantId)
      && product.availability?.state !== 'sold'
      && product.availability?.state !== 'unavailable';
  };

  const rotatedDimensions = (dimensions, rotation = {}) => {
    const turns = Math.abs(Math.round((finite(rotation.y) || 0) / 90)) % 2;
    return turns
      ? { width: dimensions.depth, depth: dimensions.width, height: dimensions.height, unit: 'in' }
      : { ...dimensions };
  };

  const boxFor = (placement = {}) => {
    const dimensions = rotatedDimensions(placement.dimensions || {}, placement.transform?.rotation || {});
    return {
      x: nonNegative(placement.transform?.position?.x),
      y: nonNegative(placement.transform?.position?.y),
      z: nonNegative(placement.transform?.position?.z),
      width: positive(dimensions.width) || 0,
      height: positive(dimensions.height) || 0,
      depth: positive(dimensions.depth) || 0
    };
  };

  const intersects = (a, b) =>
    a.x < b.x + b.width && a.x + a.width > b.x
    && a.y < b.y + b.height && a.y + a.height > b.y
    && a.z < b.z + b.depth && a.z + a.depth > b.z;

  const inside = (box, limits) =>
    box.x >= 0 && box.y >= 0 && box.z >= 0
    && box.x + box.width <= limits.width
    && box.y + box.height <= limits.height
    && box.z + box.depth <= limits.depth;

  const fitPlacement = (placement, usableSpace, existingPlacements = []) => {
    const reasons = [];
    const warnings = [];
    if (!usableSpace || usableSpace.status !== 'READY' || !usableSpace.usable) {
      return { status: FIT.UNKNOWN, reasons: ['USABLE_SPACE_UNKNOWN'], warnings, checkedAt: null };
    }
    if (!dimensionsComplete(placement?.dimensions)) {
      return { status: FIT.UNKNOWN, reasons: ['PIECE_DIMENSIONS_INCOMPLETE'], warnings, checkedAt: null };
    }
    const box = boxFor(placement);
    if (!inside(box, usableSpace.usable)) reasons.push('OUTSIDE_USABLE_BOUNDS');
    usableSpace.protectedZones.forEach((zone) => {
      if (intersects(box, zone)) reasons.push(zone.reason || 'PROTECTED_ZONE_COLLISION');
    });
    existingPlacements.filter((candidate) => candidate.id !== placement.id).forEach((candidate) => {
      if (dimensionsComplete(candidate.dimensions) && intersects(box, boxFor(candidate))) {
        reasons.push('PIECE_OVERLAP');
      }
    });
    const serviceClearance = usableSpace.enclosure.access.serviceClearance;
    if (serviceClearance > 0 && (
      box.x < serviceClearance
      || box.x + box.width > usableSpace.usable.width - serviceClearance
    )) warnings.push('SERVICE_ACCESS_TIGHT');
    if (placement.openingRequired === true && placement.openingVerified !== true) {
      warnings.push('OPENING_USE_UNVERIFIED');
    }
    return {
      status: reasons.length ? FIT.DOES_NOT_FIT : warnings.length ? FIT.FIT_WITH_WARNING : FIT.FIT,
      reasons: unique(reasons),
      warnings: unique(warnings),
      checkedAt: null,
      authority: 'DETERMINISTIC_FIT_ENGINE'
    };
  };

  const validateLayout = (placements, usableSpace) => {
    const checked = (placements || []).map((placement) => ({
      ...placement,
      fit: fitPlacement(placement, usableSpace, placements)
    }));
    const statuses = checked.map((placement) => placement.fit.status);
    const status = statuses.length === 0
      ? FIT.UNKNOWN
      : statuses.includes(FIT.DOES_NOT_FIT)
      ? FIT.DOES_NOT_FIT
      : statuses.includes(FIT.UNKNOWN)
        ? FIT.UNKNOWN
        : statuses.includes(FIT.FIT_WITH_WARNING)
          ? FIT.FIT_WITH_WARNING
          : FIT.FIT;
    return {
      status,
      placements: checked,
      counts: Object.fromEntries(Object.values(FIT).map((value) => [
        value,
        statuses.filter((statusValue) => statusValue === value).length
      ])),
      authority: 'DETERMINISTIC_FIT_ENGINE',
      aiOverrideAllowed: false
    };
  };

  const initialTransform = (dimensions, usable, index) => {
    const columns = Math.max(1, Math.floor(usable.width / Math.max(dimensions.width, 1)));
    const column = index % columns;
    const row = Math.floor(index / columns);
    return {
      position: {
        x: round(Math.min(Math.max(0, column * (dimensions.width + 0.5)), Math.max(0, usable.width - dimensions.width))),
        y: 0,
        z: round(Math.min(Math.max(0, row * (dimensions.depth + 0.5)), Math.max(0, usable.depth - dimensions.depth)))
      },
      rotation: { x: 0, y: 0, z: 0 },
      locked: false
    };
  };

  const createPlacement = (product, usableSpace, index = 0, role = null) => {
    const identity = productIdentity(product);
    const dimensions = productDimensions(product);
    return {
      id: identity.variantId || identity.pieceId || `placement-${index + 1}`,
      ...identity,
      role: role || product.compatibility?.buildRoles?.[0] || product.merchandisingRole || 'structure',
      selectionReasons: unique([
        'CURRENT_PUBLIC_ELIGIBLE_EXACT_PIECE',
        role ? `REQUESTED_ROLE_${String(role).toUpperCase()}` : 'AVAILABLE_STRUCTURE',
        dimensionsComplete(dimensions) ? 'RECORDED_BOUNDING_DIMENSIONS' : 'DIMENSIONS_INCOMPLETE'
      ]),
      dimensions,
      geometry: {
        type: 'BOUNDING_BOX_PROXY',
        honestProxy: true,
        irregularShapeNotRepresented: true
      },
      transform: usableSpace?.usable && dimensionsComplete(dimensions)
        ? initialTransform(dimensions, usableSpace.usable, index)
        : { position: { x: 0, y: 0, z: 0 }, rotation: { x: 0, y: 0, z: 0 }, locked: false },
      openingRequired: false,
      openingVerified: false
    };
  };

  const findInitialFit = (placement, usableSpace, existingPlacements = []) => {
    if (!usableSpace?.usable || !dimensionsComplete(placement.dimensions)) return placement;
    const usable = usableSpace.usable;
    for (const rotation of [0, 90]) {
      const rotated = rotatedDimensions(placement.dimensions, { y: rotation });
      const maximumX = Math.max(0, usable.width - rotated.width);
      const maximumZ = Math.max(0, usable.depth - rotated.depth);
      for (let z = 0; z <= maximumZ + 0.001; z += 0.5) {
        for (let x = 0; x <= maximumX + 0.001; x += 0.5) {
          const candidate = updateTransform(placement, {
            position: { x: round(x), y: 0, z: round(z) },
            rotation: { y: rotation }
          });
          if (fitPlacement(candidate, usableSpace, [...existingPlacements, candidate]).status !== FIT.DOES_NOT_FIT) {
            return candidate;
          }
        }
      }
    }
    return placement;
  };

  const generateLayout = ({ products = [], usableSpace, goals = [], budgetCents = null, maximumPieces = 8 } = {}) => {
    const eligible = products.filter(publicEligible).slice(0, MAX_PIECES);
    const withinBudget = [];
    let subtotal = 0;
    eligible.forEach((product) => {
      const price = productIdentity(product).priceCents || 0;
      if (withinBudget.length >= maximumPieces) return;
      if (Number.isFinite(Number(budgetCents)) && Number(budgetCents) > 0 && subtotal + price > Number(budgetCents)) return;
      withinBudget.push(product);
      subtotal += price;
    });
    const placements = [];
    withinBudget.forEach((product, index) => {
      const proposed = createPlacement(product, usableSpace, index, goals[index % Math.max(1, goals.length)] || null);
      placements.push(findInitialFit(proposed, usableSpace, placements));
    });
    const validated = validateLayout(placements, usableSpace);
    return {
      layoutId: `layout-${eligible.map((product) => productIdentity(product).variantId).join('-').slice(0, 80) || 'empty'}`,
      inventoryStatus: eligible.length ? 'CURRENT_ELIGIBLE_ONLY' : 'EMPTY',
      placements: validated.placements,
      fit: {
        status: validated.status,
        counts: validated.counts,
        authority: validated.authority,
        aiOverrideAllowed: false
      },
      subtotalCents: subtotal,
      explanation: eligible.length
        ? {
            why: 'The layout uses current public eligible exact pieces and their recorded bounding dimensions.',
            confidence: validated.status === FIT.UNKNOWN ? 'Unknown' : validated.status === FIT.DOES_NOT_FIT ? 'Low' : 'High',
            ruleSources: ['public_inventory_eligibility_v1', 'deterministic_fit_engine_v1'],
            inventorySources: ['current_shopify_public_projection'],
            limitations: ['Irregular cork is represented by conservative bounding-box proxies.']
          }
        : {
            why: 'No current eligible exact pieces were available. Your enclosure and build intent can still be saved on this device.',
            confidence: 'Unknown',
            ruleSources: ['public_inventory_eligibility_v1'],
            inventorySources: ['current_shopify_public_projection'],
            limitations: ['No products were invented and no future-wave inventory was loaded.']
          }
    };
  };

  const updateTransform = (placement, patch = {}) => ({
    ...placement,
    transform: {
      ...placement.transform,
      position: {
        ...placement.transform?.position,
        ...(patch.position || {})
      },
      rotation: {
        ...placement.transform?.rotation,
        ...(patch.rotation || {})
      },
      ...(typeof patch.locked === 'boolean' ? { locked: patch.locked } : {})
    }
  });

  const rankSubstitutions = ({ soldPlacement, products = [], usableSpace, placements = [] } = {}) => {
    if (!soldPlacement) return [];
    return products
      .filter(publicEligible)
      .filter((product) => productIdentity(product).variantId !== soldPlacement.variantId)
      .map((product) => {
        const candidate = createPlacement(product, usableSpace, 0, soldPlacement.role);
        candidate.transform = clone(soldPlacement.transform);
        const fit = fitPlacement(candidate, usableSpace, placements.filter((entry) => entry.id !== soldPlacement.id));
        const identity = productIdentity(product);
        const sameFamily = identity.family === soldPlacement.family;
        const sameRole = candidate.role === soldPlacement.role;
        const priceDelta = Math.abs((identity.priceCents || 0) - (soldPlacement.priceCents || 0));
        return {
          product,
          candidate,
          fit,
          score: (fit.status === FIT.FIT ? 400 : fit.status === FIT.FIT_WITH_WARNING ? 300 : 0)
            + (sameFamily ? 80 : 0)
            + (sameRole ? 60 : 0)
            + Math.max(0, 40 - Math.floor(priceDelta / 500)),
          reasons: unique([
            fit.status,
            sameFamily ? 'SAME_FAMILY' : null,
            sameRole ? 'SAME_ROLE' : null,
            priceDelta <= 1000 ? 'SIMILAR_PRICE' : null
          ])
        };
      })
      .filter((entry) => ![FIT.DOES_NOT_FIT, FIT.UNKNOWN].includes(entry.fit.status))
      .sort((a, b) => b.score - a.score || productIdentity(a.product).variantId.localeCompare(productIdentity(b.product).variantId));
  };

  const conceptTransition = (current, action) => {
    const transitions = {
      NOT_REQUESTED: { request: 'READY' },
      READY: { generate: 'GENERATING', cancel: 'NOT_REQUESTED' },
      GENERATING: { complete: 'COMPLETE', fail: 'FAILED' },
      COMPLETE: { expire: 'EXPIRED', request: 'READY' },
      FAILED: { retry: 'READY' },
      EXPIRED: { request: 'READY' }
    };
    return transitions[current]?.[action] || current;
  };

  const createConcept = (layout, state = 'NOT_REQUESTED') => ({
    id: layout?.layoutId ? `concept-${layout.layoutId}` : null,
    state: CONCEPT_STATES.includes(state) ? state : 'NOT_REQUESTED',
    provider: 'FIXTURE_PLACEHOLDER_ONLY',
    scaleVerified: false,
    inventoryProof: false,
    disclaimer: DISCLAIMER
  });

  const createBuildDna = ({
    previous = {},
    enclosure,
    usableSpace,
    speciesProfile = null,
    interview = {},
    layout,
    concept = null,
    substitutions = []
  } = {}) => ({
    ...previous,
    version: BUILD_DNA_VERSION,
    enclosure: clone(enclosure || null),
    usable_dimensions: clone(usableSpace?.usable || null),
    access: clone(enclosure?.access || null),
    species_profile: speciesProfile ? {
      id: speciesProfile.id,
      version: SPECIES_PROFILES.version,
      status: speciesProfile.status
    } : null,
    habitat_goals: unique(interview.goals || []),
    budget: {
      ...(previous.budget || {}),
      merchandise_limit_cents: finite(interview.budgetCents)
    },
    pieces: (layout?.placements || []).map((placement) => ({
      piece_id: placement.pieceId || null,
      product_id: placement.productId || null,
      variant_id: placement.variantId || null,
      handle: placement.handle || null,
      title: placement.title || null,
      image: placement.image || null,
      price_cents: placement.priceCents,
      family: placement.family,
      role: placement.role,
      selection_reasons: clone(placement.selectionReasons || []),
      dimensions: clone(placement.dimensions),
      geometry: clone(placement.geometry),
      transform: clone(placement.transform),
      fit: clone(placement.fit)
    })),
    protected_zones: clone(usableSpace?.protectedZones || []),
    equipment: clone(enclosure?.features?.equipment || []),
    assumptions: clone(usableSpace?.assumptions || []),
    warnings: unique((layout?.placements || []).flatMap((placement) => [
      ...(placement.fit?.warnings || []),
      ...(placement.fit?.reasons || [])
    ])),
    substitutions: substitutions.slice(0, 20).map((entry) => ({
      from_variant_id: entry.fromVariantId,
      to_variant_id: entry.toVariantId,
      confirmed: entry.confirmed === true,
      reasons: entry.reasons || []
    })),
    concept_render_id: concept?.id || null,
    concept_render_state: concept?.state || 'NOT_REQUESTED',
    layout_version: VERSION,
    authority: {
      ...(previous.authority || {}),
      cart: 'shopify',
      inventory: 'current_shopify_public_projection',
      fit: 'deterministic_fit_engine',
      placement: 'local_scaled_canvas',
      concept: 'non_authoritative_visualization',
      compatibility: 'species_profiles_fixture_unapproved'
    }
  });

  const createVisionInput = (file = {}, consent = false, calibration = null) => ({
    id: `local-${idFor(file.name) || 'image'}-${finite(file.size) || 0}`,
    name: idFor(file.name),
    type: idFor(file.type),
    size: finite(file.size),
    lastModified: finite(file.lastModified),
    consent: consent === true,
    storage: 'LOCAL_PREVIEW_ONLY',
    uploaded: false,
    exactScaleAvailable: calibration?.referenceLengthIn > 0,
    calibration: calibration?.referenceLengthIn > 0 ? clone(calibration) : null,
    analysis: {
      status: 'FIXTURE_NOT_RUN',
      provider: null,
      warning: calibration?.referenceLengthIn > 0
        ? 'Calibration is customer supplied and remains unverified.'
        : 'Exact scale cannot be inferred from an unreferenced photo.'
    }
  });

  const interviewQuestions = ({ profileId = 'other', answers = {} } = {}) => {
    const profile = SPECIES_PROFILES.profiles.find((entry) => entry.id === profileId) || SPECIES_PROFILES.profiles.at(-1);
    const base = [
      { id: 'goals', prompt: 'What should this habitat make possible?', required: true },
      { id: 'style', prompt: 'How should the build feel?', required: true },
      { id: 'budget', prompt: 'What is the merchandise budget?', required: false }
    ];
    if (answers.hasWaterFeature === true) {
      base.push({ id: 'water_clearance', prompt: 'Where must water access stay open?', required: true });
    }
    if (answers.hasMountedEquipment === true) {
      base.push({ id: 'equipment_clearance', prompt: 'Which equipment zones must remain clear?', required: true });
    }
    return { profile, questions: [...base, ...profile.questions.map((id) => ({
      id,
      prompt: humanize(id),
      required: false
    }))] };
  };

  const createEvent = (name, detail = {}) => ({
    name,
    version: 'customer-habitat-events-v1',
    occurredAt: null,
    destination: null,
    sent: false,
    privacy: 'PUBLIC_OR_CUSTOMER_SUPPLIED_LOCAL_CONTEXT',
    detail: clone(detail)
  });

  return Object.freeze({
    VERSION,
    BUILD_DNA_VERSION,
    FIT,
    CONCEPT_STATES,
    DISCLAIMER,
    ENCLOSURE_CATALOG,
    SPECIES_PROFILES,
    normalizeDimensions,
    dimensionsComplete,
    createEnclosure,
    calculateUsableSpace,
    productDimensions,
    productIdentity,
    publicEligible,
    rotatedDimensions,
    fitPlacement,
    validateLayout,
    createPlacement,
    findInitialFit,
    generateLayout,
    updateTransform,
    rankSubstitutions,
    conceptTransition,
    createConcept,
    createBuildDna,
    createVisionInput,
    interviewQuestions,
    createEvent,
    humanize
  });
});
